import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";

import useUserModules from "hooks/useUserModules";

import {
  PlayRadiusDefault,
  createPlayRadiusDefault,
  deletePlayRadiusDefault,
  fetchPlayRadiusDefaults,
  savePlayRadiusDefault
} from "api/ipdp";

export const PLAY_RADIUS_DEFAULTS_QUERY_KEY = "playRadiusDefaults";

export default function usePlayRadiusDefaults() {
  const queryClient = useQueryClient();

  const { availablePlays } = useUserModules();

  const query = useQuery<PlayRadiusDefault[]>({
    queryKey: [PLAY_RADIUS_DEFAULTS_QUERY_KEY],
    queryFn: fetchPlayRadiusDefaults,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    select: (data) => [...data].sort((a, b) => a.play.localeCompare(b.play)),
    onError: () => {
      queryClient.setQueryData([PLAY_RADIUS_DEFAULTS_QUERY_KEY], []);
    }
  });

  const createMutation = useMutation(
    (entries: PlayRadiusDefault[]) => createPlayRadiusDefault(entries),
    {
      onSuccess: (created) => {
        queryClient.setQueryData<PlayRadiusDefault[]>(
          [PLAY_RADIUS_DEFAULTS_QUERY_KEY],
          [...created].sort((a, b) => a.play.localeCompare(b.play))
        );
      },
      onError: () => {
        toast.error("Failed to add play.");
      }
    }
  );

  const saveMutation = useMutation(
    (entry: PlayRadiusDefault) => savePlayRadiusDefault(entry),
    {
      onSuccess: (updated) => {
        queryClient.setQueryData<PlayRadiusDefault[]>(
          [PLAY_RADIUS_DEFAULTS_QUERY_KEY],
          [...updated].sort((a, b) => a.play.localeCompare(b.play))
        );
        toast.success("Radius defaults saved.");
      },
      onError: () => {
        toast.error("Failed to save radius defaults.");
      }
    }
  );

  const deleteMutation = useMutation((play: string) => deletePlayRadiusDefault(play), {
    onSuccess: () => {
      queryClient.invalidateQueries([PLAY_RADIUS_DEFAULTS_QUERY_KEY]);
      toast.success("Play removed.");
    },
    onError: () => {
      toast.error("Failed to remove play.");
    }
  });

  return {
    defaults: query.data ?? [],
    isLoading: query.isLoading,
    availablePlays,
    create: createMutation.mutate,
    createAsync: createMutation.mutateAsync,
    save: saveMutation.mutate,
    saveAsync: saveMutation.mutateAsync,
    remove: deleteMutation.mutate
  };
}
